import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/index.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Pricing from "@/pages/Pricing";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import SuperAdminHospitals from "@/pages/superadmin/Hospitals";
import SuperAdminPlans from "@/pages/superadmin/Plans";
import SuperAdminSubscriptions from "@/pages/superadmin/Subscriptions";
import SuperAdminCoupons from "@/pages/superadmin/Coupons";
import SuperAdminPaymentGateways from "@/pages/superadmin/PaymentGateways";
import SuperAdminSaaSEmail from "@/pages/superadmin/SaaSEmail";
import SuperAdminBackupsAndDunning from "@/pages/superadmin/BackupsAndDunning";
import SuperAdminGstSettings from "@/pages/superadmin/GstSettings";
import SuperAdminStaff from "@/pages/superadmin/Staff";
import SuperAdminMasters from "@/pages/superadmin/Masters";
import PrintGstInvoice from "@/pages/print/PrintGstInvoice";
import MyPlan from "@/pages/admin/MyPlan";
import Checkout from "@/pages/admin/Checkout";
import Patients from "@/pages/patients/Patients";
import PatientNew from "@/pages/patients/PatientNew";
import PatientDetail from "@/pages/patients/PatientDetail";
import OpdQueue from "@/pages/opd/OpdQueue";
import OpdConsultation from "@/pages/opd/OpdConsultation";
import Optometry from "@/pages/optometry/Optometry";
import Billing from "@/pages/billing/Billing";
import NewInvoice from "@/pages/billing/NewInvoice";
import InvoiceView from "@/pages/billing/InvoiceView";
import AbdmCenter from "@/pages/abdm/AbdmCenter";
import Reports from "@/pages/reports/Reports";
import AdminUsers from "@/pages/admin/Users";
import AdminBranches from "@/pages/admin/Branches";
import AdminMasters from "@/pages/admin/Masters";
import AdminSettings from "@/pages/admin/Settings";
import AuditLog from "@/pages/Audit";
import PrintConsultation from "@/pages/print/PrintConsultation";
import PrintInvoice from "@/pages/print/PrintInvoice";
import PrintDischarge from "@/pages/print/PrintDischarge";
import Landing from "@/pages/Landing";  // eslint-disable-line no-unused-vars -- kept for future use
import OtSchedule from "@/pages/ot/OtSchedule";
import OtNote from "@/pages/ot/OtNote";
import OtImplants from "@/pages/ot/OtImplants";
import Lab from "@/pages/lab/Lab";
import Pharmacy from "@/pages/pharmacy/Pharmacy";
import IpdAdmissions from "@/pages/ipd/IpdAdmissions";
import IpdDetail from "@/pages/ipd/IpdDetail";
import Panel from "@/pages/panel/Panel";
import Reminders from "@/pages/admin/Reminders";
import SelectBranch from "@/pages/SelectBranch";
import ResetBranchPin from "@/pages/ResetBranchPin";
import Profile from "@/pages/Profile";
import Appointments from "@/pages/appointments/Appointments";
import PrintRegistrationReceipt from "@/pages/print/PrintRegistrationReceipt";
import PrintOpdSticker from "@/pages/print/PrintOpdSticker";
import PrintIpdSticker from "@/pages/print/PrintIpdSticker";
import PrintPrescriptionCard from "@/pages/print/PrintPrescriptionCard";
import PrintAdmissionConsent from "@/pages/print/PrintAdmissionConsent";
import PrintNursingNotes from "@/pages/print/PrintNursingNotes";
import PrintRefundReceipt from "@/pages/print/PrintRefundReceipt";
import PrintReceipt from "@/pages/print/PrintReceipt";
import PrintPharmacyInvoice from "@/pages/print/PrintPharmacyInvoice";
import PrintPharmacyReceipt from "@/pages/print/PrintPharmacyReceipt";
import PrintPharmacyReport from "@/pages/print/PrintPharmacyReport";
import PrintOtNote from "@/pages/print/PrintOtNote";
import IpdBill from "@/pages/billing/IpdBill";
import PublicBoard from "@/pages/board/PublicBoard";
import Legal from "@/pages/legal/Legal";

function RootRedirect() {
  const { user } = useAuth();
  if (user === undefined) return null;
  return <Navigate to={user ? "/app" : "/"} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/select-branch" element={<SelectBranch />} />
          <Route path="/reset-branch-pin" element={<ResetBranchPin />} />
          <Route path="/legal" element={<Navigate to="/legal/terms" replace />} />
          <Route path="/legal/:doc" element={<Legal />} />

          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="profile" element={<Profile />} />

            {/* Super Admin / SaaS staff — frontend just lets them in; backend require_roles guards write actions */}
            <Route
              path="superadmin/hospitals"
              element={<ProtectedRoute roles={["super_admin", "saas_admin", "saas_manager", "saas_user"]}><SuperAdminHospitals /></ProtectedRoute>}
            />
            <Route
              path="superadmin/plans"
              element={<ProtectedRoute roles={["super_admin", "saas_admin", "saas_manager", "saas_user"]}><SuperAdminPlans /></ProtectedRoute>}
            />
            <Route
              path="superadmin/subscriptions"
              element={<ProtectedRoute roles={["super_admin", "saas_admin", "saas_manager", "saas_user"]}><SuperAdminSubscriptions /></ProtectedRoute>}
            />
            <Route
              path="superadmin/coupons"
              element={<ProtectedRoute roles={["super_admin", "saas_admin", "saas_manager"]}><SuperAdminCoupons /></ProtectedRoute>}
            />
            <Route
              path="superadmin/payment-gateways"
              element={<ProtectedRoute roles={["super_admin", "saas_admin"]}><SuperAdminPaymentGateways /></ProtectedRoute>}
            />
            <Route
              path="superadmin/saas-email"
              element={<ProtectedRoute roles={["super_admin", "saas_admin"]}><SuperAdminSaaSEmail /></ProtectedRoute>}
            />
            <Route
              path="superadmin/backups"
              element={<ProtectedRoute roles={["super_admin", "saas_admin"]}><SuperAdminBackupsAndDunning /></ProtectedRoute>}
            />
            <Route
              path="superadmin/gst-settings"
              element={<ProtectedRoute roles={["super_admin", "saas_admin"]}><SuperAdminGstSettings /></ProtectedRoute>}
            />
            <Route
              path="superadmin/staff"
              element={<ProtectedRoute roles={["super_admin", "saas_admin", "saas_manager", "saas_user"]}><SuperAdminStaff /></ProtectedRoute>}
            />
            <Route
              path="superadmin/masters"
              element={<ProtectedRoute roles={["super_admin", "saas_admin", "saas_manager", "saas_user"]}><SuperAdminMasters /></ProtectedRoute>}
            />

            {/* Hospital subscription billing */}
            <Route
              path="my-plan"
              element={<ProtectedRoute roles={["hospital_admin", "branch_admin", "it_admin"]}><MyPlan /></ProtectedRoute>}
            />
            <Route
              path="checkout/:planId"
              element={<ProtectedRoute roles={["hospital_admin", "branch_admin", "it_admin"]}><Checkout /></ProtectedRoute>}
            />

            {/* Patient + OPD */}
            <Route path="patients" element={<Patients />} />
            <Route path="patients/new" element={<PatientNew />} />
            <Route path="patients/:id" element={<PatientDetail />} />
            <Route path="opd" element={<OpdQueue />} />
            <Route path="opd/consult/:visitId" element={<OpdConsultation />} />
            <Route path="optometry" element={<Optometry />} />
            <Route path="appointments" element={<Appointments />} />

            {/* Billing */}
            <Route path="billing" element={<Billing />} />
            <Route path="billing/new" element={<NewInvoice />} />
            <Route path="billing/:id" element={<InvoiceView />} />

            {/* ABDM */}
            <Route path="abdm" element={<AbdmCenter />} />

            {/* Reports */}
            <Route path="reports" element={<Reports />} />

            {/* Admin */}
            <Route
              path="admin/users"
              element={<ProtectedRoute roles={["super_admin", "hospital_admin", "branch_admin", "it_admin"]}><AdminUsers /></ProtectedRoute>}
            />
            <Route
              path="admin/branches"
              element={<ProtectedRoute roles={["super_admin", "hospital_admin"]}><AdminBranches /></ProtectedRoute>}
            />
            <Route
              path="admin/masters"
              element={<ProtectedRoute roles={["super_admin", "hospital_admin", "branch_admin", "it_admin"]}><AdminMasters /></ProtectedRoute>}
            />
            <Route
              path="admin/settings"
              element={<ProtectedRoute roles={["super_admin", "hospital_admin", "branch_admin", "it_admin"]}><AdminSettings /></ProtectedRoute>}
            />

            <Route path="audit" element={<AuditLog />} />

            {/* OT */}
            <Route path="ot" element={<OtSchedule />} />
            <Route path="ot/implants" element={<OtImplants />} />
            <Route path="ot/:id" element={<OtNote />} />

            {/* Lab */}
            <Route path="lab" element={<Lab />} />

            {/* Pharmacy */}
            <Route path="pharmacy" element={<Pharmacy />} />

            {/* IPD */}
            <Route path="ipd" element={<IpdAdmissions />} />
            <Route path="ipd/:id" element={<IpdDetail />} />
            <Route path="ipd/:admissionId/bill" element={<IpdBill />} />

            {/* Panel / TPA */}
            <Route path="panel" element={<Panel />} />

            {/* Reminders (admin) */}
            <Route
              path="admin/reminders"
              element={<ProtectedRoute roles={["super_admin", "hospital_admin", "branch_admin", "it_admin"]}><Reminders /></ProtectedRoute>}
            />
          </Route>

          {/* Printable views */}
          <Route path="/print/consultation/:visitId" element={<ProtectedRoute><PrintConsultation /></ProtectedRoute>} />
          <Route path="/print/invoice/:id" element={<ProtectedRoute><PrintInvoice /></ProtectedRoute>} />
          <Route path="/print/gst-invoice/:id" element={<ProtectedRoute><PrintGstInvoice /></ProtectedRoute>} />
          <Route path="/print/discharge/:id" element={<ProtectedRoute><PrintDischarge /></ProtectedRoute>} />
          <Route path="/print/registration-receipt/:invoiceId" element={<ProtectedRoute><PrintRegistrationReceipt /></ProtectedRoute>} />
          <Route path="/print/opd-sticker/:patientId" element={<ProtectedRoute><PrintOpdSticker /></ProtectedRoute>} />
          <Route path="/print/ipd-sticker/:admissionId" element={<ProtectedRoute><PrintIpdSticker /></ProtectedRoute>} />
          <Route path="/print/prescription-card/:patientId" element={<ProtectedRoute><PrintPrescriptionCard /></ProtectedRoute>} />
          <Route path="/print/ipd-consent/:admissionId" element={<ProtectedRoute><PrintAdmissionConsent /></ProtectedRoute>} />
          <Route path="/print/nursing-notes/:admissionId" element={<ProtectedRoute><PrintNursingNotes /></ProtectedRoute>} />
          <Route path="/print/refund-receipt/:invoiceId/:refundNo" element={<ProtectedRoute><PrintRefundReceipt /></ProtectedRoute>} />
          <Route path="/print/refund-receipt/:invoiceId" element={<ProtectedRoute><PrintRefundReceipt /></ProtectedRoute>} />
          <Route path="/print/receipt/:invoiceId/:idx" element={<ProtectedRoute><PrintReceipt /></ProtectedRoute>} />
          <Route path="/print/pharm/:id" element={<ProtectedRoute><PrintPharmacyInvoice /></ProtectedRoute>} />
          <Route path="/print/pharm-receipt/:saleId/:idx" element={<ProtectedRoute><PrintPharmacyReceipt /></ProtectedRoute>} />
          <Route path="/print/pharmacy-report/:type" element={<ProtectedRoute><PrintPharmacyReport /></ProtectedRoute>} />
          <Route path="/print/ot/:id" element={<ProtectedRoute><PrintOtNote /></ProtectedRoute>} />
          <Route path="/board/:branchId" element={<PublicBoard />} />

          <Route path="*" element={<RootRedirect />} />
        </Routes>
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
